import { useSaveActions } from "../../common"
import { TableName } from "../../../tables"
import { define } from "../../../utils/typeUtils"
import { useSystemStateHooks } from "../../system"
import { Data2, newId, useDataTypeFileActions } from "../../../features/data-types"
import { Currency, DataType } from "../../../tables/types/types"
import { Equivalence, Lot, Product, ProductPrices } from "../state/types"
import { useProductPricesActions } from "./productPricesActions_NEW"
import { O } from "../../../features/ui"
import { NO_LOT } from "../utils/lotUtils"
import { useLotActions } from "./lotActions"
import { useBranchLoaders } from "../../manager"
import { Stock, useStockActions } from "../../stock"
import { useProductPricesLoaders } from "../loaders/productPricesLoaders"
import { useLotLoaders } from "../loaders/lotLoaders"
import { useProductLoaders } from "../loaders/productLoaders"
import { usePurchasesActions, usePurchasesHooks } from "../../purchases"
import { Discount } from "../../common"

export const useProductActions = () => {
    const { companyId } = useSystemStateHooks()
    const { getLinkedProductIds } = usePurchasesHooks()

    const productLoaders = useProductLoaders()
    const branchLoaders = useBranchLoaders()
    const productPricesLoaders = useProductPricesLoaders()
    const lotLoaders = useLotLoaders()

    const saveActions = useSaveActions()
    const dataTypeFileActions = useDataTypeFileActions()
    const { saveProductPrices } = useProductPricesActions()
    const { saveLot } = useLotActions()
    const { saveStock } = useStockActions()
    const purchasesActions = usePurchasesActions()

    const saveProduct = (product: Product) => {
        const { save } = saveActions()
        return save(TableName.PRODUCTS, product) as Promise<Product>
    }

    const saveProducts = (products: Product[]) => {
        const { saveMultiple } = saveActions()
        return saveMultiple(TableName.PRODUCTS, products) as Promise<Product[]>
    }

    const saveProductWithImage = async (
        product: Product,
        image: File,
        imageProp = 'imageUrl'
    ) => {
        const { saveWithImage } = dataTypeFileActions(define(companyId))
        return saveWithImage(TableName.PRODUCTS, product, imageProp as keyof DataType, image)
    }

    const createOrUpdateProduct = async (
        product: Product,
        pricesData: Data2,
        imageFile?: File
    ) => {
        const definedCompanyId = define(companyId)
        
        const { saveWithImage } = dataTypeFileActions(companyId)
        const savedProduct = await saveWithImage(TableName.PRODUCTS, product, 'imageUrl' as keyof DataType, imageFile) as Product
        
        const { loadProductPrices } = productPricesLoaders()
        const loadedPrices = await loadProductPrices(product.id)
        const { purchasePrice, vatTaxId, saleYield, bonuses } = pricesData
        const productPrices: ProductPrices = loadedPrices ? {
            ...loadedPrices,
            purchasePrice: define(purchasePrice) as number,
            vatTaxId: define(vatTaxId) as string,
            saleYield: saleYield as O<number>,
            bonuses: bonuses as Discount[]
        } : {
            companyId: definedCompanyId,
            id: newId(),
            productId: product.id,
            purchasePrice: define(purchasePrice) as number,
            vatTaxId: define(vatTaxId) as string,
            saleYield: saleYield as O<number>,
            bonuses: bonuses as Discount[],
            currency: Currency.ARS
        }
        await saveProductPrices(productPrices)

        const { loadProductLots } = lotLoaders()
        const loadedLots = await loadProductLots(product.id)
        if (loadedLots.length === 0) {
            const baseLot: Lot = {
                companyId: definedCompanyId,
                id: newId(),
                code: NO_LOT,
                productId: product.id
            }
            const lot = await saveLot(baseLot)

            const { loadAllBranches } = branchLoaders()
            const branches = await loadAllBranches()
            await Promise.all(
                branches.map(async branch => {
                    const stock: Stock = {
                        companyId: definedCompanyId,
                        id: newId(),
                        productId: product.id,
                        lotId: lot.id,
                        availableUnits: 0,
                        reservedUnits: 0,
                        currency: Currency.ARS,
                        branchId: branch.id
                    }
                    await saveStock(stock)
                })
            )
        }

        return savedProduct
    }

    const linkProducts = async (
        productId_1: string,
        productId_2: string,
        equivalence: number
    ) => {
        const { loadProduct } = productLoaders()
        const loadedProduct = define(await loadProduct(productId_1))
        
        const loadedEquivalences = loadedProduct.equivalences || []
        const equivalenceMatches = (loadedEquivalence: Equivalence) => loadedEquivalence.productId === productId_2
        const equivalenceExists = !!loadedEquivalences.find(equivalenceMatches)
        const newEquivalence = { productId: productId_2, equivalence }
        const newEquivalences = equivalenceExists ?
            loadedEquivalences.map(loadedEquivalence => equivalenceMatches(loadedEquivalence) ? newEquivalence : loadedEquivalence) :
            [...loadedEquivalences, newEquivalence]
        const updatedProduct: Product = {
            ...loadedProduct,
            equivalences: newEquivalences
        }
        
        const savedProduct = await saveProduct(updatedProduct)

        const linkedProductIds = getLinkedProductIds()
        const updatedLinkedProductIds = linkedProductIds.includes(productId_1) ? linkedProductIds : [...linkedProductIds, productId_1]
        const { savePurchasesSetting } = purchasesActions()
        await savePurchasesSetting('linkedProductIds', updatedLinkedProductIds)

        return savedProduct
    }

    const unlinkProducts = async (
        productId_1: string,
        productId_2: string
    ) => {
        const { loadProduct } = productLoaders()
        const loadedProduct = define(await loadProduct(productId_1))

        const newEquivalences = loadedProduct.equivalences?.filter(equivalence => equivalence.productId !== productId_2) || []
        const updatedProduct: Product = {
            ...loadedProduct,
            equivalences: newEquivalences.length > 0 ? newEquivalences : undefined
        }
        
        const savedProduct = await saveProduct(updatedProduct)

        if (!newEquivalences || newEquivalences?.length === 0) {
            const linkedProductIds = getLinkedProductIds()
            const updatedLinkedProductIds = linkedProductIds.filter(productId => productId !== productId_1)
            const { savePurchasesSetting } = purchasesActions()
            await savePurchasesSetting('linkedProductIds', updatedLinkedProductIds)
        }

        return savedProduct
    }

    const removeProduct = async (id: string) => {
        const { remove } = saveActions()
        await remove(TableName.PRODUCTS, id)
    }

    const removeProductWithImage = async (id: string) => {
        const { removeWithImage } = dataTypeFileActions(define(companyId))
        await removeWithImage(TableName.PRODUCTS, id, 'imageUrl' as keyof DataType)
    }

    return {
        saveProduct,
        saveProducts,
        saveProductWithImage,
        createOrUpdateProduct,
        linkProducts,
        unlinkProducts,
        removeProduct,
        removeProductWithImage
    }
}
